import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { MARKER_TYPES, MapCategory, ZONE_TYPES, Zone, ZoneTypeDefinition } from "@/types/mapping"
import { formatDistanceToNow } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const adjustColor = (color: string, amount: number) => {
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
};

export const getMarkerColor = (type: keyof typeof MARKER_TYPES) => {
  return MARKER_TYPES[type]?.color || MARKER_TYPES.DEFAULT.color;
};

export function calculateTotalArea(zones?: Zone[]) {
  if (!zones || !Array.isArray(zones)) {
    return 0;
  }

  // First identify all zones
  const superZones = zones.filter(z => z.isSuper);
  const regularZones = zones.filter(z => !z.isSuper && !z.parentSuperZoneId);

  // Calculate total area:
  // 1. Include super zones' entire area
  // 2. Include regular zones (not under super zones)
  // 3. Exclude child zones to prevent double counting
  return [
    ...superZones,
    ...regularZones
  ].reduce((sum, zone) => sum + (zone.area || 0), 0);
}

export function calculateMowableArea(zones?: Zone[]) {
  if (!zones || !Array.isArray(zones)) {
    return 0;
  }

  // First identify all zones
  const superZones = zones.filter(z => z.isSuper);
  const childZones = zones.filter(z => !z.isSuper && z.parentSuperZoneId);
  const regularZones = zones.filter(z => !z.isSuper && !z.parentSuperZoneId);

  // Helper function to check if zone is mowable
  const isMowable = (zone: any) => {
    // Exclude zones that are explicitly not mowable
    if (zone.type === 'stay_out' || 
        zone.type === 'bunker' || 
        zone.type === 'water' || 
        zone.type === 'cart_path') {
      return false;
    }
    
    // Check if zone type allows mowers
    const golfZoneType = ZONE_TYPES['GOLF']?.find(t => t.id === zone.type);
    const normalZoneType = ZONE_TYPES['NORMAL']?.find(t => t.id === zone.type);
    return (golfZoneType?.allowsMowers || normalZoneType?.allowsMowers) || false;
  };

  // Calculate mowable area:
  // 1. For super zones that are mowable, include their entire area
  // 2. For regular zones (not under super zones) that are mowable, include their area
  // 3. Exclude child zones to prevent double counting
  const mowableArea = [
    ...superZones.filter(isMowable),
    ...regularZones.filter(isMowable)
  ].reduce((sum, zone) => sum + (zone.area || 0), 0);

  return mowableArea;
}

export interface MowerRecommendation {
  model: string;
  brand: string;
  type: string;
  quantity: number;
  coverage: number;
}

export const calculateRecommendedMowers = (area: number, zoneType: string, mapCategory: MapCategory = 'NORMAL') => {
  // Check if zone type allows mowers
  const zoneTypeDefinition = ZONE_TYPES[mapCategory]?.find((zt: ZoneTypeDefinition) => zt.id === zoneType);
  if (!zoneTypeDefinition?.allowsMowers) {
    return [];
  }

  const recommendations: MowerRecommendation[] = [];
  let remainingArea = area;

  if (zoneType.toLowerCase().includes('slope')) {
    const awd535Units = Math.ceil(area / 1.2);
    return [{
      model: "HUSQVARNA_535_AWD",
      brand: "Husqvarna",
      type: "AWD",
      quantity: awd535Units,
      coverage: 1.2
    }];
  }

  while (remainingArea > 0) {
    if (remainingArea > 15) {
      recommendations.push({
        model: "CEORA",
        brand: "Husqvarna",
        type: "Commercial",
        quantity: 1,
        coverage: 15
      });
      remainingArea -= 15;
    } else if (remainingArea > 8) {
      recommendations.push({
        model: "CEORA",
        brand: "Husqvarna",
        type: "Commercial",
        quantity: 1,
        coverage: remainingArea
      });
      remainingArea = 0;
    } else if (remainingArea > 4) {
      recommendations.push({
        model: "236",
        brand: "Kress",
        type: "Professional",
        quantity: 1,
        coverage: Math.min(8, remainingArea)
      });
      remainingArea = 0;
    } else if (remainingArea > 1.5) {
      recommendations.push({
        model: "233",
        brand: "Kress",
        type: "Professional",
        quantity: 1,
        coverage: Math.min(4, remainingArea)
      });
      remainingArea = 0;
    } else {
      recommendations.push({
        model: "520 EPOS",
        brand: "Husqvarna",
        type: "Professional",
        quantity: 1,
        coverage: remainingArea
      });
      remainingArea = 0;
    }
  }

  return recommendations;
};

export interface MowerStatistics {
  totalArea: number;
  totalMowers: number;
  mowersByType: Record<string, number>;
  mowersByModel: Record<string, number>;
  mowersByBrand: Record<string, number>;
  recommendedMowers: MowerRecommendation[];
}

export function calculateMowerStatistics(zones: Zone[]): MowerStatistics {
  const stats: MowerStatistics = {
    totalArea: 0,
    totalMowers: 0,
    mowersByType: {},
    mowersByModel: {},
    mowersByBrand: {},
    recommendedMowers: []
  };

  zones.forEach(zone => {
    if (!zone.area) return;
    stats.totalArea += zone.area;
    
    // If zone has assigned mowers, count them
    if (zone.mowers && zone.mowers.length > 0) {
      zone.mowers.forEach(mower => {
        stats.totalMowers += mower.quantity;
        stats.mowersByType[mower.type] = (stats.mowersByType[mower.type] || 0) + mower.quantity;
        stats.mowersByModel[mower.model] = (stats.mowersByModel[mower.model] || 0) + mower.quantity;
        stats.mowersByBrand[mower.brand] = (stats.mowersByBrand[mower.brand] || 0) + mower.quantity;
      });
    } else {
      // If no mowers assigned, calculate recommended mowers
      const recommended = calculateRecommendedMowers(zone.area, zone.type);
      stats.recommendedMowers.push(...recommended);
      
      // Add recommended mowers to totals
      recommended.forEach(mower => {
        stats.totalMowers += mower.quantity;
        stats.mowersByType[mower.type] = (stats.mowersByType[mower.type] || 0) + mower.quantity;
        stats.mowersByModel[mower.model] = (stats.mowersByModel[mower.model] || 0) + mower.quantity;
        stats.mowersByBrand[mower.brand] = (stats.mowersByBrand[mower.brand] || 0) + mower.quantity;
      });
    }
  });

  return stats;
}

export const calculateArea = (points: google.maps.LatLngLiteral[]): number => {
  if (!points || points.length < 3) return 0;
  
  // Implementation using Google Maps geometry library
  const path = points.map(point => new google.maps.LatLng(point.lat, point.lng));
  return google.maps.geometry.spherical.computeArea(path) * 0.000247105; // Convert to acres
};

export const calculateDistance = (points: google.maps.LatLngLiteral[]): number => {
  if (!points || points.length < 2) return 0;
  
  // Implementation using Google Maps geometry library
  const path = points.map(point => new google.maps.LatLng(point.lat, point.lng));
  return google.maps.geometry.spherical.computeLength(path) * 3.28084; // Convert to feet
};

export const getDefaultColorForZoneType = (type: string): string => {
  const colors: Record<string, string> = {
    standard: '#4CAF50',
    'no-mow': '#FF5252',
    field: '#2196F3',
    practice: '#FFC107',
    lawn: '#8BC34A',
    restricted: '#F44336',
    yard: '#4CAF50',
    garden: '#FF9800'
  };
  
  return colors[type.toLowerCase()] || '#808080';
};

export function calculateTotalMowers(zones: Zone[], category: MapCategory = 'NORMAL') {
  if (!zones) return 0;
  
  // Get mowable zones
  const mowableZones = zones.filter(zone => {
    const golfZoneType = ZONE_TYPES['GOLF'].find(t => t.id === zone.type);
    const normalZoneType = ZONE_TYPES['NORMAL'].find(t => t.id === zone.type);
    return (golfZoneType?.allowsMowers || normalZoneType?.allowsMowers) || false;
  });
  
  // Calculate mowers needed for each zone
  const recommendations = mowableZones.flatMap(zone => 
    calculateRecommendedMowers(zone.area, zone.type, category)
  );
  
  // Sum up all mower quantities
  return recommendations.reduce((total, rec) => total + rec.quantity, 0);
} 

export function calculateSlopedArea(zones?: Zone[]) {
  if (!zones || !Array.isArray(zones)) {
    return 0;
  }

  return zones
    .filter(zone => {
      // Check if zone type includes 'slope' in its name
      return zone.type?.toLowerCase().includes('slope') || false;
    })
    .reduce((sum, zone) => sum + (zone.area || 0), 0);
} 

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
} 

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1
  }).format(value);
} 

export function formatFirestoreDate(date: any) {
  if (!date) return '';
  // Handle Firestore Timestamp
  if (date?.toDate instanceof Function) {
    return formatDistanceToNow(date.toDate(), { addSuffix: true });
  }
  // Handle ISO string
  if (typeof date === 'string') {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  }
  // Handle Date object
  if (date instanceof Date) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  return '';
} 